/** Utility functions for adding tasks, separated from React hook logic. */
// @ts-check
import { db, runTransaction, serverTimestamp } from '@/lib/firebase';
import { getNextOrderKey } from '@/features/tasks/utils/structure/taskHierarchy';
import { safeGenerateKeyBetween } from '@/features/tasks/utils/interactions/listKeyHelpers';
import { ensureTermsExist as checkAndAddTerms } from '@/services/tagService';
import { buildNameMapForTerms } from '@/services/tagNameUtils';
import { newTaskSchema } from '@/features/tasks/utils/data/taskSchema';
import { parseTaskInput } from '@/features/tasks/utils/data/taskParser';
import { getTask } from '@/services/taskService';
import { createTask } from '@/services/taskMutations';
import { withDurationReviewOnCreate } from '@/features/tasks/utils/data/durationReviewUtils';
import { logTaskCreated } from '@/services/analyticsService';

/**
 * @param {string | Partial<NewTask>} input
 * @returns {Partial<NewTask> | null}
 */
const parseAndValidateInput = (input) =>
  typeof input === 'string' ? parseTaskInput(input) : input;

/**
 * Append-at-end when nextKey is absent; insert-between when nextKey is set.
 * @param {any} pos
 */
const isAppendAtEnd = (pos) => {
  if (!pos) return true;
  const hasPosition =
    pos.previousKey !== undefined || pos.nextKey !== undefined;
  if (!hasPosition) return true;
  return pos.nextKey === null || pos.nextKey === undefined;
};

/**
 * Computes the order key for a new task.
 *
 * Append operations (no nextKey bound) always go through the per-parent
 * orderKey anchor inside a Firestore transaction so panel, list, and
 * calendar adds share the same serialization and cannot reuse a key.
 * `pos.previousKey` is passed as a floor hint for filtered-list appends.
 * Insert-between (explicit nextKey) uses safeGenerateKeyBetween directly.
 *
 * @param {any} pos
 * @param {string} userId
 * @param {string | null} parentId
 * @returns {Promise<string>}
 */
const computeOrderKey = async (pos, userId, parentId) => {
  if (isAppendAtEnd(pos)) {
    const floorKey = pos?.previousKey ?? null;
    return runTransaction(db, (transaction) =>
      getNextOrderKey(userId, parentId, transaction, floorKey),
    );
  }
  return safeGenerateKeyBetween(pos.previousKey || null, pos.nextKey || null);
};

/**
 * @param {string | null} id
 * @returns {Promise<Task | null>}
 */
const fetchParent = async (id) => {
  if (!id) return null;
  return await getTask(id);
};

/**
 * @param {Partial<NewTask>} data
 * @param {string | null} parentId
 * @returns {Promise<Task | null>}
 */
const inheritMetadata = async (data, parentId) => {
  const p = await fetchParent(parentId);
  if (!p) return null;
  if (!data.projects?.length && p.projects?.length)
    data.projects = [...p.projects];
  if (!data.contexts?.length && p.contexts?.length)
    data.contexts = [...p.contexts];
  return p;
};

/**
 * @param {any} payload
 * @returns {NewTask}
 */
const validateTaskPayload = (payload) => {
  const res = newTaskSchema.safeParse(payload);
  if (res.success) return res.data;
  const flat = res.error.flatten();
  throw new Error(
    Object.values(flat.fieldErrors).flat()[0] ||
      flat.formErrors[0] ||
      'Ongeldige data.',
  );
};

/**
 * @param {any} user
 * @param {string | Partial<NewTask>} input
 * @param {any} pos
 * @returns {Promise<string | null>}
 */
export const executeAddTask = async (user, input, pos = null) => {
  if (!user?.uid) return null;
  try {
    const data = parseAndValidateInput(input);
    if (!data) return null;
    const parentId = pos?.parentId || data.parentId || null;
    const key = await computeOrderKey(pos, user.uid, parentId);
    const parent = await inheritMetadata(data, parentId);
    const parentPath =
      parent?.materializedPath ||
      (parent?.orderKey ? `${parent.orderKey}.` : '');
    const path = `${parentPath}${key}.`;
    await checkAndAddTerms(
      data.projects,
      'projects',
      user.uid,
      buildNameMapForTerms(data.projects, data.projectNames),
    );
    await checkAndAddTerms(
      data.contexts,
      'contexts',
      user.uid,
      buildNameMapForTerms(data.contexts, data.contextNames),
    );
    const durationExplicitlyProvided =
      data.duration !== undefined && data.duration !== null;
    const requireTimeReviewExplicit = Object.prototype.hasOwnProperty.call(
      data,
      'requireTimeReview',
    );
    const payload = {
      ...data,
      userId: user.uid,
      parentId,
      orderKey: key,
      materializedPath: path,
      rescheduleCount: data.date ? 1 : 0,
    };
    const validated = validateTaskPayload(payload);
    const taskToCreate = withDurationReviewOnCreate(validated, {
      durationExplicitlyProvided,
      requireTimeReviewExplicit,
    });
    const taskId = await createTask({
      ...taskToCreate,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    logTaskCreated();
    return taskId;
  } catch (e) {
    console.error('ADD_TASK ERROR:', e);
    throw e;
  }
};
